import { ensurePlusSdkLoaded } from '../../lib/plus/plusClient';
import { JABAZ_PLUS_PRODUCT, JABAZ_PRICE_TOMAN, JABAZ_SUBSCRIPTION_DAYS } from './constants';
import type { JabazSubscription } from './types';

/**
 * Real payment for the queue subscription through Paziresh24 Plus (plus.paziresh24.com), product
 * JABAZ_PLUS_PRODUCT: our own checkout page, Plus's headless SDK underneath — the patient pays from the
 * wallet, or tops it up through the Katibe gateway and comes back to the checkout page, where the SDK
 * finishes the payment by itself. Plus keeps the subscription, so it is the same on every device, and the
 * queue service (dabbeh) asks Plus (server to server) before seating anyone.
 * Empty JABAZ_PLUS_PRODUCT = the prototype's instant purchase.
 *
 * The SDK script is the one the site already loads for Plus (src/lib/plus/plusClient.ts); the queue only
 * adds its own instance, on its own screens (sheet, checkout).
 */
export const PLUS_BASE_URL = 'https://plus.paziresh24.com';

export const isRealBilling = () => !!JABAZ_PLUS_PRODUCT;

const DAY = 24 * 3600_000;

export interface PlusPlan {
  id: string;
  months: number;
  /** toman */
  amount: number;
}

export interface PlusStatus {
  active: boolean;
  customerId?: string;
  entitlements?: Array<{ id?: string }>;
  /** unix seconds */
  current_period_end?: number | null;
}

export interface PlusInstance {
  getPlans: () => Promise<PlusPlan[]>;
  getStatus: () => Promise<PlusStatus>;
  startCheckout: (priceId: string, options?: { autoRenew?: boolean }) => Promise<{ ok?: boolean; status?: PlusStatus; redirecting?: boolean } | undefined>;
  closePaywall: (immediate?: boolean) => void;
  resumeIfNeeded: () => Promise<void>;
}

let instance: Promise<PlusInstance> | null = null;

/**
 * plus.js with `create()`, for the queue only. Plus's CDN still serves an older SDK (no `create()`) for the
 * plain address and for the one the site loads (`?v=sheet-54-iframe-host`). Then a current copy is loaded
 * under a daily version, and the site's own `window.plus` is put back at once — so the site's Plus host
 * (iframes, Nelson…) stays exactly as it was.
 */
const loadCurrentSdk = () =>
  new Promise<any>((resolve, reject) => {
    const previous = (window as any).plus;
    const script = document.createElement('script');
    script.src = `${PLUS_BASE_URL}/sdk/plus.js?v=jabaz-${new Date().toISOString().slice(0, 10)}`;
    script.async = true;
    script.dataset.jabazPlus = 'true';
    script.onload = () => {
      const current = (window as any).plus;
      if (previous) (window as any).plus = previous;
      if (typeof current?.create === 'function') resolve(current);
      else reject(new Error('plus.js without create()'));
    };
    script.onerror = () => reject(new Error('plus.js did not load'));
    document.head.appendChild(script);
  });

/** This product's own Plus instance (other teams' Plus products on the page stay apart). */
export const loadPlus = (): Promise<PlusInstance> => {
  if (!isRealBilling()) return Promise.reject(new Error('plus off'));
  if (instance) return instance;
  instance = ensurePlusSdkLoaded()
    .catch(() => null)
    .then(root => (typeof (root as any)?.create === 'function' ? root : loadCurrentSdk()))
    .then(root => (root as any).create({ productName: JABAZ_PLUS_PRODUCT, baseUrl: PLUS_BASE_URL }) as PlusInstance)
    .catch(error => {
      instance = null;
      throw error;
    });
  return instance;
};

/** Plus's status → our subscription (null when not active). */
export const toJabazSubscription = (status: PlusStatus | null | undefined, pricePaid = JABAZ_PRICE_TOMAN): JabazSubscription | null => {
  if (!status?.active) return null;
  const raw = typeof status.current_period_end === 'number' && status.current_period_end > 0 ? status.current_period_end : null;
  // unix seconds as documented; a value already in milliseconds is taken as it is (never «۲۰ هزار روز مانده»)
  const end = raw ? (raw > 1e12 ? raw : raw * 1000) : Date.now() + JABAZ_SUBSCRIPTION_DAYS * DAY;
  if (end <= Date.now()) return null;
  return {
    startedAt: new Date(end - JABAZ_SUBSCRIPTION_DAYS * DAY).toISOString(),
    endsAt: new Date(end).toISOString(),
    trackingCode: String(status.entitlements?.[0]?.id ?? status.customerId ?? ''),
    pricePaid,
  };
};

/** The one-month plan (falls back to the first plan Plus offers). */
export const fetchPlan = async (): Promise<PlusPlan | null> => {
  if (!isRealBilling()) return null;
  try {
    const plans = await (await loadPlus()).getPlans();
    return plans.find(plan => plan.months === 1) ?? plans[0] ?? null;
  } catch {
    return null;
  }
};

/** The plan's price in toman, or our constant when Plus is off or unreachable. */
export const fetchPlanPrice = async (): Promise<number> => {
  const plan = await fetchPlan();
  return plan && Number.isFinite(plan.amount) && plan.amount > 0 ? plan.amount : JABAZ_PRICE_TOMAN;
};

/** The patient's active subscription on Plus, or null (none, not logged in, or Plus off / unreachable). */
export const fetchSubscription = async (): Promise<JabazSubscription | null> => {
  if (!isRealBilling()) return null;
  try {
    return toJabazSubscription(await (await loadPlus()).getStatus(), await fetchPlanPrice());
  } catch {
    return null;
  }
};

/**
 * The patient's subscription as Plus sees it now: the subscription, `null` when Plus says there is none,
 * `undefined` when Plus could not be asked (off, not loaded, unreachable) — then nothing is decided.
 */
export const checkSubscription = async (): Promise<JabazSubscription | null | undefined> => {
  if (!isRealBilling()) return undefined;
  try {
    return toJabazSubscription(await (await loadPlus()).getStatus(), await fetchPlanPrice());
  } catch {
    return undefined;
  }
};

/** Back from the Katibe gateway: Plus put its resume marker in the address. */
export const isReturningFromGateway = (search: string) => /[?&](plus_resume_id|plus_status)=/.test(search);

/**
 * The address this page was opened with, read when this code first runs — before the site's own Plus host
 * (src/lib/plus/plusClient.ts, default instance) runs its resume on boot: finding no payment of its own, it
 * strips `plus_resume_id` from the address, and the queue's instance would then never finish the payment.
 * Taken once (a later visit of the page in the same tab is not a return from the gateway).
 */
const landing = typeof window !== 'undefined' ? { path: window.location.pathname, search: window.location.search } : null;
let landingTaken = false;
export const takeLandingSearch = (path: string) => {
  if (landingTaken || !landing || landing.path !== path) return '';
  landingTaken = true;
  return landing.search;
};

/**
 * Finishes this product's pending payment after the gateway: puts the resume marker back in the address and
 * lets the queue's instance resume at once (it reads and strips the marker in the same step).
 */
export const resumeAfterGateway = async (plus: PlusInstance, search: string) => {
  const resumeId = new URLSearchParams(search).get('plus_resume_id');
  if (!resumeId) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get('plus_resume_id') !== resumeId) {
    url.searchParams.set('plus_resume_id', resumeId);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  await plus.resumeIfNeeded().catch(() => undefined);
};
export const gatewayFailed = (search: string) =>
  /cancel|fail|error|unpaid|reject|unsuccess/i.test(new URLSearchParams(search).get('plus_status') ?? '');
